import { useI18n } from '@/hooks/useI18n';
import type {
  AdminCategoryOption,
  AdminRoleOption,
  CategoryAssignment,
} from '@/features/owner/adminUsersTypes';

const ROLE_PLATFORM_CATEGORY: Record<string, string> = {
  OPERATIONS_PARTNER: 'OPERATIONS_PARTNER',
  RESEARCH_PARTNER: 'RESEARCH_PARTNER',
  DEPARTMENT_HEAD: 'DEPARTMENT_HEAD',
  DEPARTMENT_ASSISTANT: 'DEPARTMENT_ASSISTANT',
  DOCTORAL_STUDENT: 'DOCTORAL_STUDENT',
  LEARNER: 'DOCTORAL_STUDENT',
  STUDENT: 'SUPERVISED_STUDENT',
  COURSE_STUDENT: 'COURSE_STUDENT',
  AUTHOR: 'AUTHOR',
  CLIENT: 'CLIENT',
  PROJECT_EDITOR: 'PROJECT_EDITOR',
  PROJECT_MEMBER: 'PROJECT_MEMBER',
};

export function accessCategoriesForRoles(
  roles: string[],
  current: CategoryAssignment[],
): CategoryAssignment[] {
  const needed = new Set(
    roles
      .map((role) => ROLE_PLATFORM_CATEGORY[role])
      .filter((code): code is string => Boolean(code)),
  );
  const kept = current.filter((item) => needed.has(item.category_code));
  for (const code of needed) {
    if (!kept.some((item) => item.category_code === code)) {
      kept.push({ category_code: code, metadata: {} });
    }
  }
  return kept;
}

interface RoleCategoryEditorProps {
  roles: AdminRoleOption[];
  categories: AdminCategoryOption[];
  selectedRoles: string[];
  selectedCategories: CategoryAssignment[];
  onRolesChange: (roles: string[]) => void;
  onCategoriesChange: (categories: CategoryAssignment[]) => void;
  lockSystemOwner?: boolean;
  /** Owner invite form: one access-category list. Server roles stay mapped from it. */
  accessOnly?: boolean;
}

export const RoleCategoryEditor = ({
  roles,
  categories,
  selectedRoles,
  selectedCategories,
  onRolesChange,
  onCategoriesChange,
  lockSystemOwner,
  accessOnly = false,
}: RoleCategoryEditorProps) => {
  const { locale, t } = useI18n();

  const toggleRole = (code: string) => {
    const turningOff = selectedRoles.includes(code);
    const nextRoles = turningOff
      ? selectedRoles.filter((item) => item !== code)
      : [...selectedRoles, code];
    onRolesChange(nextRoles);
    if (!accessOnly) return;
    const platform = ROLE_PLATFORM_CATEGORY[code];
    if (!platform) return;
    if (turningOff) {
      const stillNeeded = nextRoles.some((role) => ROLE_PLATFORM_CATEGORY[role] === platform);
      if (!stillNeeded) {
        onCategoriesChange(selectedCategories.filter((item) => item.category_code !== platform));
      }
      return;
    }
    if (!selectedCategories.some((item) => item.category_code === platform)) {
      onCategoriesChange([...selectedCategories, { category_code: platform, metadata: {} }]);
    }
  };

  const toggleCategory = (code: string) => {
    const exists = selectedCategories.some((item) => item.category_code === code);
    if (exists) {
      onCategoriesChange(selectedCategories.filter((item) => item.category_code !== code));
    } else {
      onCategoriesChange([...selectedCategories, { category_code: code, metadata: {} }]);
    }
  };

  const setMetadata = (code: string, key: string, value: unknown) => {
    onCategoriesChange(
      selectedCategories.map((item) =>
        item.category_code === code
          ? { ...item, metadata: { ...item.metadata, [key]: value } }
          : item,
      ),
    );
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div>
        <p className="owner-access-note">
          {accessOnly ? t('admin.users.field.accessCategories') : t('admin.users.field.roles')}
        </p>
        <div className="owner-access-rolegrid">
          {roles.map((role) => (
            <label key={role.code} className="owner-access-roleopt">
              <input
                type="checkbox"
                checked={selectedRoles.includes(role.code)}
                disabled={lockSystemOwner && role.code === 'SYSTEM_OWNER'}
                onChange={() => toggleRole(role.code)}
              />
              <span>
                <strong>{role.label[locale]}</strong>
                <br />
                <small style={{ color: '#7f9eaf' }}>{role.caption[locale]}</small>
              </span>
            </label>
          ))}
        </div>
      </div>

      {accessOnly ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {Array.from(
            new Set(
              selectedRoles
                .map((role) => ROLE_PLATFORM_CATEGORY[role])
                .filter((code): code is string => Boolean(code)),
            ),
          ).map((code) => {
            const category = categories.find((item) => item.code === code);
            const assignment = selectedCategories.find((item) => item.category_code === code);
            if (!category || category.fields.length === 0 || !assignment) return null;
            return (
              <div key={code} className="owner-access-category-block">
                <p className="owner-access-note">{category.label[locale]}</p>
                <div className="owner-access-grid2">
                  {category.fields.map((field) => (
                    <label key={field.key} className="owner-access-field">
                      <span>
                        {field.label[locale]}
                        {field.required ? ' *' : ''}
                      </span>
                      {field.type === 'select' ? (
                        <select
                          value={String(assignment.metadata[field.key] ?? '')}
                          onChange={(event) => setMetadata(code, field.key, event.target.value)}
                        >
                          <option value="">{t('admin.users.field.notAssigned')}</option>
                          {field.options.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label[locale]}
                            </option>
                          ))}
                        </select>
                      ) : field.type === 'boolean' ? (
                        <input
                          type="checkbox"
                          checked={Boolean(assignment.metadata[field.key])}
                          onChange={(event) => setMetadata(code, field.key, event.target.checked)}
                        />
                      ) : (
                        <input
                          type="text"
                          value={String(assignment.metadata[field.key] ?? '')}
                          onChange={(event) => setMetadata(code, field.key, event.target.value)}
                        />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      <div>
        <p className="owner-access-note">{t('admin.users.field.categories')}</p>
        <div style={{ display: 'grid', gap: 10 }}>
          {categories.map((category) => {
            const assignment = selectedCategories.find(
              (item) => item.category_code === category.code,
            );
            const checked = Boolean(assignment);
            return (
              <div key={category.code} className="owner-access-category-block">
                <label className="owner-access-roleopt" style={{ border: 'none', padding: 0 }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleCategory(category.code)}
                  />
                  <span>
                    <strong>{category.label[locale]}</strong>
                    <br />
                    <small style={{ color: '#7f9eaf' }}>{category.caption[locale]}</small>
                  </span>
                </label>
                {checked && category.fields.length > 0 ? (
                  <div className="owner-access-grid2" style={{ marginTop: 10 }}>
                    {category.fields.map((field) => (
                      <label key={field.key} className="owner-access-field">
                        <span>
                          {field.label[locale]}
                          {field.required ? ' *' : ''}
                        </span>
                        {field.type === 'select' ? (
                          <select
                            value={String(assignment?.metadata[field.key] ?? '')}
                            onChange={(event) =>
                              setMetadata(category.code, field.key, event.target.value)
                            }
                          >
                            <option value="">{t('admin.users.field.notAssigned')}</option>
                            {field.options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label[locale]}
                              </option>
                            ))}
                          </select>
                        ) : field.type === 'boolean' ? (
                          <input
                            type="checkbox"
                            checked={Boolean(assignment?.metadata[field.key])}
                            onChange={(event) =>
                              setMetadata(category.code, field.key, event.target.checked)
                            }
                          />
                        ) : (
                          <input
                            type="text"
                            value={String(assignment?.metadata[field.key] ?? '')}
                            onChange={(event) =>
                              setMetadata(category.code, field.key, event.target.value)
                            }
                          />
                        )}
                      </label>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
};
